/// <amd-module name="Feed/Core/Redux/FeedHub/FeedHubReducers" />
import { ActionsOfType } from "redux/Utils/ActionHelper";

import { Uri } from "VSS/Platform/Util/Url";

import { Feed, FeedRole, FeedView } from "Feed/Common/Contracts/Feed";
import { FeedHubActions, FeedHubActionTypes } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import produce from "immer";

import { IFeedHubDataState, IFeedHubUiState } from "./FeedHubContracts";

export function initialFeedHubState(): IFeedHubDataState {
    return {
        feeds: {},
        packagingUrl: "",
        sessionTokenUri: new Uri("")
    };
}

function setSelectedFeed(draft: IFeedHubDataState, feedFullyQualifiedId: string) {
    draft.selectedFeedId = undefined;
    draft.selectedViewId = undefined;

    const parts = feedFullyQualifiedId.split("@");
    if (parts.length === 0) {
        return;
    }
    const selectedFeed = draft.feeds[parts[0]];
    if (selectedFeed != undefined) {
        // set selectedFeedId
        draft.selectedFeedId = parts[0];
        if (parts.length === 2 && selectedFeed.views != undefined) {
            const selectedView = selectedFeed.views[parts[1]];
            if (selectedView != undefined) {
                // set selectedViewId
                draft.selectedViewId = selectedView.id;
            }
        }

        // TODO: Move permission logic from FeedManager to here
    }
}

export const feedStateReducer = produce((draft: IFeedHubDataState, action: FeedHubActions) => {
    switch (action.type) {
        case FeedHubActionTypes.InitialDataLoaded:
            initialDataLoaded(draft, action);
            break;

        case FeedHubActionTypes.FeedSelected:
            const fullyQualifiedId = action.payload;
            setSelectedFeed(draft, fullyQualifiedId);
            break;

        case FeedHubActionTypes.FeedAdded:
            const feed = action.payload;
            draft.feeds[feed.id] = {
                feed,
                views: undefined
            };
            break;

        case FeedHubActionTypes.GetFeedCompleted:
            updateFeed(draft, action.payload);
            break;

        case FeedHubActionTypes.GetFeedPermissionsCompleted:
            updateFeedPermissions(draft, action);
            break;

        case FeedHubActionTypes.GetFeedViewsCompleted:
            updateFeedViews(draft, action);
            break;

        case FeedHubActionTypes.FeedDeleted:
            const deletedFeed = draft.feeds[action.payload].feed;
            // Fake the deleted date and scheduled permanent deleted date
            deletedFeed.deletedDate = new Date();
            deletedFeed.scheduledPermanentDeleteDate = new Date();
            deletedFeed.scheduledPermanentDeleteDate.setDate(deletedFeed.deletedDate.getDate() + 30);
            draft.feeds[deletedFeed.id].feed = deletedFeed;
            break;

        case FeedHubActionTypes.FeedRestored:
            const restoredFeed = draft.feeds[action.payload].feed;
            // Faking the deleted date once returned success from the server
            // @ts-ignore Type 'null' is not assignable to Date
            restoredFeed.deletedDate = null;
            // @ts-ignore Type 'null' is not assignable to Date
            restoredFeed.scheduledPermanentDeleteDate = null;
            draft.feeds[restoredFeed.id].feed = restoredFeed;
            break;

        case FeedHubActionTypes.FeedPermanentDeleted:
            delete draft.feeds[action.payload];
            break;

        case FeedHubActionTypes.FeedUpdated:
            updateFeed(draft, action.payload);
            break;

        case FeedHubActionTypes.SetFeedMessages:
            setFeedMessages(draft, action);
            break;

        case FeedHubActionTypes.DeleteFeedMessage:
            deleteFeedMessage(draft, action);
            break;

        case FeedHubActionTypes.SetUpstreamStatuses:
            draft.feeds[action.payload.feedId].upstreamHealthStatuses = action.payload.upstreamStatuses;
            break;
    }
}, initialFeedHubState());

function initialDataLoaded(
    draft: IFeedHubDataState,
    action: ActionsOfType<FeedHubActions, FeedHubActionTypes.InitialDataLoaded>
) {
    const { feedsResult, packagingUrl, sessionTokenUri } = action.payload;
    const { feeds, feedViews, selectedFeed } = feedsResult;

    draft.packagingUrl = packagingUrl;
    draft.sessionTokenUri = sessionTokenUri;
    draft.feeds = {};
    feeds.forEach(feed => {
        draft.feeds[feed.id] = {
            feed,
            views: undefined
        };
    });
    let feedToSelect: Feed | undefined;
    const feedDataToSelect = draft.feeds[selectedFeed.id];
    if (feedDataToSelect != undefined) {
        feedToSelect = feedDataToSelect.feed;
    }
    const views: { [key: string]: FeedView } = {};
    if (feedToSelect != undefined && feedViews != null) {
        // dataprovider contains the views for the selectedFeed
        feedViews.forEach(feedView => {
            views[feedView.id] = feedView;
            if (selectedFeed.viewId == feedView.id) {
                draft.selectedViewId = selectedFeed.viewId;

                // the feeds[] entry is initialized to this view, clear those values

                // @ts-ignore Type 'undefined' is not assignable to string
                feedToSelect.view = undefined;
                // @ts-ignore Type 'null' is not assignable to string
                feedToSelect.viewId = null;
                // @ts-ignore Type 'null' is not assignable to string
                feedToSelect.viewName = null;

                feedToSelect!.fullyQualifiedId = feedToSelect!.id;
                feedToSelect!.fullyQualifiedName = feedToSelect!.name;
            }
        });
        const accessibleViewId = selectAccessibleView(feedToSelect, views);
        draft.feeds[feedToSelect.id] = {
            feed: feedToSelect,
            views,
            accessibleViewId
        };
    }

    draft.selectedFeedId = selectedFeed.id;
}

function updateFeed(draft: IFeedHubDataState, newFeedState: Feed) {
    draft.feeds[newFeedState.id].feed = newFeedState;
}

function updateFeedPermissions(
    draft: IFeedHubDataState,
    action: ActionsOfType<FeedHubActions, FeedHubActionTypes.GetFeedPermissionsCompleted>
) {
    const { feedId, feedPermissions } = action.payload;
    const feedData = draft.feeds[feedId];

    if (feedData == undefined) {
        return;
    }

    feedData.feed.permissions = feedPermissions;
    draft.feeds[feedId] = feedData;
}

function updateFeedViews(
    draft: IFeedHubDataState,
    action: ActionsOfType<FeedHubActions, FeedHubActionTypes.GetFeedViewsCompleted>
) {
    const { feedId, feedViews } = action.payload;
    const feedData = draft.feeds[feedId];

    if (feedData == undefined) {
        return;
    }

    // no views, or user doesn't have access to any views (neither are expected)
    if (feedViews.length === 0) {
        feedData.views = {};
        feedData.accessibleViewId = undefined;
        draft.feeds[feedId] = feedData;
        return;
    }

    let feedViewMap: { [key: string]: FeedView } = {};
    feedViews.forEach(feedView => {
        feedViewMap[feedView.id] = feedView;
    });

    const accessibleViewId = selectAccessibleView(feedData.feed, feedViewMap);
    feedData.views = feedViewMap;
    feedData.accessibleViewId = accessibleViewId;
    draft.feeds[feedId] = feedData;
    return;
}

function selectAccessibleView(feed: Feed, feedViews: { [key: string]: FeedView }): string | undefined {
    // Those who have permission greater than reader start at the base feed
    const shouldAccessBaseFeed =
        feed.permissions &&
        feed.permissions.length > 0 &&
        feed.permissions[0].role !== FeedRole.None &&
        feed.permissions[0].role !== FeedRole.Reader;
    if (shouldAccessBaseFeed) {
        return undefined;
    }

    // there is no default view set for this feed
    if (feed.defaultViewId == null) {
        return Object.keys(feedViews)[0];
    }

    let defaultView = feedViews[feed.defaultViewId];
    if (defaultView == undefined) {
        // user doesn't have access to default view
        return Object.keys(feedViews)[0];
    }
    // user has access to default view
    return defaultView.id;
}

function setFeedMessages(
    draft: IFeedHubDataState,
    action: ActionsOfType<FeedHubActions, FeedHubActionTypes.SetFeedMessages>
) {
    const { feedId, feedMessages } = action.payload;
    draft.feeds[feedId].feedMessages = feedMessages;
}

function deleteFeedMessage(
    draft: IFeedHubDataState,
    action: ActionsOfType<FeedHubActions, FeedHubActionTypes.DeleteFeedMessage>
) {
    const { feedId, messageIndex } = action.payload;
    const feedMessages = draft.feeds[feedId].feedMessages;
    if (feedMessages && feedMessages.length > messageIndex) {
        feedMessages.splice(messageIndex, 1);
    }
}

export const feedUIReducer = produce(
    (draft: IFeedHubUiState, action: FeedHubActions) => {
        switch (action.type) {
            case FeedHubActionTypes.InitialDataLoaded:
                const { isPublic, isAnonymous } = action.payload;
                draft.isAnonymous = isAnonymous;
                draft.isPublic = isPublic;
                break;

            case FeedHubActionTypes.SetErrorMessage:
                draft.errorMessage = action.payload;
                break;

            case FeedHubActionTypes.ShowGlobalSettings:
                draft.showGlobalSettings = true;
                break;

            case FeedHubActionTypes.HideGlobalSettings:
                draft.showGlobalSettings = false;
                break;

            case FeedHubActionTypes.CreateDialog:
                draft.dialogData = action.payload;
                break;

            case FeedHubActionTypes.DialogLoading:
                draft.dialogData!.isLoading = true;
                break;

            case FeedHubActionTypes.DismissDialog:
                draft.dialogData = undefined;
                break;

            case FeedHubActionTypes.ShowCreateFeed:
                draft.showCreateFeed = true;
                break;

            case FeedHubActionTypes.HideCreateFeed:
                draft.showCreateFeed = false;
                break;

            case FeedHubActionTypes.ShowIvyPromote:
                draft.showIvyPromote = action.payload;
                break;

            case FeedHubActionTypes.HideIvyPromote:
                delete draft.showIvyPromote;
                break;
        }
    },
    {
        showGlobalSettings: false,
        showCreateFeed: false,
        errorMessage: undefined,
        dialogData: undefined,
        isAnonymous: true,
        isPublic: false
    }
);
